import type { ScoreLandingPageResponse } from "../../../shared/score2026/scoreLandingPage.types";
import {
  attachSyllabusPdfLinks,
  findScoreLandingSyllabusWidget,
} from "../../../shared/score2026";
import { isClockSynced, serverNow } from "../../../shared/serverClock";
import {
  getScoreAuthContext,
  hasScoreBannerBlockingPackage,
  hasScoreStudentRegistration,
} from "@repo/utils";
import { useEffect, useMemo } from "react";
import { useScoreLandingPageRemoteConfig } from "./useScoreLandingPageRemoteConfig";
import {
  customInstance,
  type StudentRegistrationRecordsDataItem,
  type StudentRegistrationRecords,
} from "@oneapp/data";
import { useQuery } from "@tanstack/react-query";
import {
  scoreBannerCombinedOrdersQueryKey,
  scoreBannerStudentRecordsQueryKey,
} from "../../../shared/scoreBannerQueryKeys";

function getNow() {
  return isClockSynced() ? serverNow() : Date.now();
}

type CombinedOrdersResponse = {
  data?: {
    oms?: Array<{
      mySubcriptionDtoList?: Array<{
        product?: { name?: string | null } | null;
      }> | null;
    }>;
  };
};

export type UseScoreBannerConfigurationParams = {
  authToken: string | null | undefined;
  profilesData: Parameters<typeof getScoreAuthContext>[0] | null | undefined;
};

export type ScoreBannerConfiguration = {
  isIlVertical: boolean;
  isScoreOfflineRegistered: boolean;
  isScoreOnlineRegistered: boolean;
  onlineScoreSlots: StudentRegistrationRecordsDataItem[];
  scoreProgramDisplaySlot: StudentRegistrationRecordsDataItem | undefined;
  nonScoreProgramDisplaySlot: StudentRegistrationRecordsDataItem | undefined;
  hasAnyOnlineRegistration: boolean;
  isActiveFreeUser: boolean;
  getNow: () => number;
  hasScoreProgramAccess: boolean;
  isCombinedOrdersError: boolean;
  scoreLandingPage: ScoreLandingPageResponse;
  isScoreLandingPageInitialized: boolean;
  isScoreRegistrationReady: boolean;
};

export function useScoreBannerConfiguration({
  authToken,
  profilesData,
}: UseScoreBannerConfigurationParams): ScoreBannerConfiguration {
  const { verticalId, userId, uamId } = useMemo(
    () => getScoreAuthContext(profilesData),
    [profilesData],
  );
  const isIlVertical = verticalId === 1;
  const apisEnabled = Boolean(authToken && isIlVertical && userId && uamId);

  const studentRecordsQuery = useQuery({
    queryKey: scoreBannerStudentRecordsQueryKey(userId),
    enabled: apisEnabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    queryFn: async () => {
      const response = await customInstance<StudentRegistrationRecords>(
        {
          url: "/student/getStudentRecordsByUserId",
          method: "GET",
          params: { userId: uamId },
        },
        { clientName: "eos" },
      );
      return Array.isArray(response?.data) ? response.data : [];
    },
  });

  useEffect(() => {
    if (apisEnabled) void studentRecordsQuery.refetch();
  }, [apisEnabled, userId, studentRecordsQuery.refetch]);

  const combinedOrdersQuery = useQuery({
    queryKey: scoreBannerCombinedOrdersQueryKey(uamId),
    enabled: apisEnabled,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const response = await customInstance<CombinedOrdersResponse>(
        {
          url: "/get-user-combined-orders",
          method: "GET",
          params: { uam_id: uamId },
        },
        { clientName: "oneOms" },
      );
      return response?.data?.oms ?? [];
    },
  });

  const studentRecords = useMemo(() => {
    if (!studentRecordsQuery.data?.length) return studentRecordsQuery.data;
    return [...studentRecordsQuery.data].sort(
      (a, b) =>
        new Date(a.datetime ?? "").getTime() -
        new Date(b.datetime ?? "").getTime(),
    );
  }, [studentRecordsQuery.data]);

  const { scoreLandingPage, isInitialized: isScoreLandingPageInitialized } =
    useScoreLandingPageRemoteConfig();

  const studentRecordsWithSyllabus = useMemo(() => {
    const syllabus = findScoreLandingSyllabusWidget(scoreLandingPage);
    return attachSyllabusPdfLinks(studentRecords, syllabus);
  }, [scoreLandingPage, studentRecords]);

  const {
    isScoreOfflineRegistered,
    isScoreOnlineRegistered,
    onlineScoreSlots,
    scoreProgramDisplaySlot,
    nonScoreProgramDisplaySlot,
    hasAnyOnlineRegistration,
  } = useMemo(
    () => hasScoreStudentRegistration(studentRecordsWithSyllabus, getNow),
    [studentRecordsWithSyllabus],
  );

  const { isActiveFreeUser, isCombinedOrdersError } = useMemo(() => {
    return {
      isActiveFreeUser: hasScoreBannerBlockingPackage(
        combinedOrdersQuery.data,
        getNow,
      ),
      isCombinedOrdersError: combinedOrdersQuery.isError,
    };
  }, [combinedOrdersQuery.data]);

  const hasScoreProgramAccess = useMemo(
    () => !(!isActiveFreeUser && !isScoreOnlineRegistered),
    [isActiveFreeUser, isScoreOnlineRegistered],
  );

  const isScoreRegistrationReady =
    !apisEnabled || studentRecordsQuery.isFetched;

  return {
    isIlVertical,
    isScoreOfflineRegistered,
    isScoreOnlineRegistered,
    onlineScoreSlots,
    scoreProgramDisplaySlot,
    nonScoreProgramDisplaySlot,
    hasAnyOnlineRegistration,
    isActiveFreeUser,
    getNow,
    hasScoreProgramAccess,
    isCombinedOrdersError,
    scoreLandingPage,
    isScoreLandingPageInitialized,
    isScoreRegistrationReady,
  };
}
